import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Mail, MessageSquarePlus, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { SEO } from "@/components/SEO";

export default function Support() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
        title="Support & Help | Grounded"
        description="Get help, share feedback, or contact the Grounded team about finding work-friendly cafes."
        path="/support"
      />
      <Header />
      <main className="flex-1 container max-w-2xl mx-auto px-6 py-10 md:py-16">
        <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Browse
        </Link>

        <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
          Support
        </h1>
        <p className="text-muted-foreground text-lg mb-10 leading-relaxed">
          Have a question, found a bug, or just want to share feedback? We're here to help.
        </p>

        <div className="space-y-8">
          {/* Contact */}
          <section className="rounded-xl border border-border bg-card p-6 space-y-3">
            <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Contact Us
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              For any questions, issues, or account-related requests, reach out to us at:
            </p>
            <a
              href="mailto:support@groundedtheapp.com"
              className="inline-block text-primary font-medium hover:underline"
            >
              support@groundedtheapp.com
            </a>
            <div className="flex items-center gap-4 pt-1">
              <a
                href="https://instagram.com/groundedtheapp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Instagram: @groundedtheapp
              </a>
              <a
                href="https://threads.net/@groundedtheapp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Threads: @groundedtheapp
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              We typically respond within 24–48 hours.
            </p>
          </section>

          {/* In-App Feedback */}
          <section className="rounded-xl border border-border bg-card p-6 space-y-3">
            <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-primary" />
              Send Feedback
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Found something broken? Have an idea for a feature? Use the feedback form to let us know directly.
            </p>
            <Button variant="outline" onClick={() => setFeedbackOpen(true)}>
              Open Feedback Form
            </Button>
          </section>

          {/* FAQ */}
          <section className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h2 className="font-display text-xl font-semibold text-foreground">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground">How do I delete my account?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Go to your <Link to="/profile" className="text-primary hover:underline">Profile</Link> → Settings tab → scroll to "Delete My Account." This permanently removes all your data.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-foreground">How are Grounded Scores calculated?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Grounded Scores are based on what each café offers — WiFi, outlet availability, seating comfort, and parking — combined with real community check-in data to give you an accurate picture of how work-friendly a spot really is.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-foreground">Can I suggest a café?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Yes! Use the "Suggest a Cafe" button in the footer or on the Browse page. Our team reviews all submissions.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-foreground">Do I need an account to browse cafés?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  No — you can browse, view details, and see scores without signing in. An account is needed for check-ins, reviews, and favorites.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
